/**
 * ChatFlows Skills Integration for AI Compliance Wizard
 *
 * This file contains all the skill handlers that connect the ChatFlows embed
 * to the compliance page wizard functionality.
 *
 * Active Skills:
 * 1. save_company_name - Saves company name
 * 2. save_industry - Saves industry type
 * 3. save_location - Saves location / country
 * 4. save_team_size - Saves total staff count
 * 5. save_equipment - Saves machinery/plant/vehicles info
 * 6. save_risks - Saves primary risks
 * 7. save_project_description - Saves project description
 * 8. save_work_setting - Saves type of work setting
 * 9. save_worker_roles - Saves worker roles + counts (JSONB)
 * 10. save_hazardous_substances - Saves hazardous substances info
 * 11. save_environment - Saves indoor/outdoor details
 * 12. save_heights - Saves heights/structures info
 * 13. save_incident_history - Saves incident history file ID (optional)
 * 14. save_accreditations - Saves preferred accreditations (optional)
 * 15. update_requirements - Updates generated requirements (add/remove/replace/amend)
 * 16. generate_requirements - Triggers final compliance generation
 *
 * Deprecated Skills:
 * - complete_section - No longer used (sections removed from wizard flow)
 *
 * Note: All fields are saved directly to collectedData without nested section structure
 */

/**
 * Register all ChatFlows skills for the compliance wizard
 *
 * @param {Object} chatFlowsRef - Ref to ChatFlowsEmbed component
 * @param {Object} context - Context object with state and handlers
 */
export const registerComplianceSkills = (chatFlowsRef, context) => {
  const {
    // State setters
    setCompanyName,
    setIndustry,
    setLocation,
    setTeamSize,
    setEquipment,
    setRisks,
    setProjectDescription,
    setSpecialRequirements,
    setWorkSetting,
    setWorkerRoles,
    setHazardousSubstances,
    setEnvironment,
    setHeights,
    setIncidentHistoryFileId,
    setAccreditations,
    // Current state values
    companyName,
    industry,
    location,
    teamSize,
    equipment,
    risks,
    projectDescription,
    specialRequirements,
    workSetting,
    workerRoles,
    hazardousSubstances,
    environment,
    heights,
    incidentHistoryFileId,
    accreditations,
    setIsGenerating,
    projectId,
    teamMembers,
    qualifications,
    setQualifications,
    complianceService,
    generateRequirementsFromWizard,
    getAllEmbedData
  } = context;

  if (!chatFlowsRef.current) {
    console.warn('ChatFlows ref not available');
    return;
  }

  console.log('📋 Registering ChatFlows compliance skills...');

  // Helper function to save a single field directly (no sections)
  const saveData = async (field, value) => {
    console.log(`💾 saveData called:`, { field, value });

    if (value === undefined) {
      console.error('❌ saveData: value is undefined');
      return {
        success: false,
        error: 'Missing value parameter'
      };
    }

    // Convert team_size to integer if it's a string or number
    let processedValue = value;
    if (field === 'team_size') {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue)) {
        console.error('❌ saveData: team_size value is not a valid number:', value);
        return {
          success: false,
          error: `Invalid team_size value: ${value}. Must be a number.`
        };
      }
      processedValue = numValue;
      console.log(`🔢 Converted team_size from "${value}" to number:`, processedValue);
    }

    try {
      // Update the appropriate state setter based on field name
      const fieldSetters = {
        company_name: setCompanyName,
        industry: setIndustry,
        location: setLocation,
        team_size: setTeamSize,
        equipment: setEquipment,
        risks: setRisks,
        project_description: setProjectDescription,
        special_requirements: setSpecialRequirements,
        work_setting: setWorkSetting,
        worker_roles: setWorkerRoles,
        hazardous_substances: setHazardousSubstances,
        environment: setEnvironment,
        heights: setHeights,
        incident_history_file_id: setIncidentHistoryFileId,
        accreditations: setAccreditations
      };

      const setter = fieldSetters[field];
      if (!setter) {
        throw new Error(`Unknown field: ${field}`);
      }

      console.log(`📝 Updating field "${field}" to`, processedValue);
      setter(processedValue);

      // Save to database - only the changed field
      console.log(`💾 Saving field "${field}" to database:`, processedValue);
      await complianceService.saveWizardData(projectId, {
        [field]: processedValue
      });

      console.log(`✅ Saved ${field}="${processedValue}"`);

      // Update embed context - always send all data
      if (chatFlowsRef.current?.update && getAllEmbedData) {
        const allEmbedData = getAllEmbedData();
        chatFlowsRef.current.update(allEmbedData);
      }

      return {
        success: true,
        message: `Saved ${field}`,
        data: { field, value: processedValue }
      };
    } catch (error) {
      console.error(`❌ Error saving ${field}:`, error);
      return {
        success: false,
        error: error.message
      };
    }
  };

  // ==========================================
  // Skill 1: save_company_name
  // ==========================================
  chatFlowsRef.current.registerSkill('save_company_name', async (args) => {
    console.log('🔵 save_company_name called:', args);
    return await saveData('company_name', args.value);
  });

  // ==========================================
  // Skill 2: save_industry
  // ==========================================
  chatFlowsRef.current.registerSkill('save_industry', async (args) => {
    console.log('🔵 save_industry called:', args);
    return await saveData('industry', args.value);
  });

  // ==========================================
  // Skill 3: save_location
  // ==========================================
  chatFlowsRef.current.registerSkill('save_location', async (args) => {
    console.log('🔵 save_location called:', args);
    return await saveData('location', args.value);
  });

  // ==========================================
  // Skill 4: save_team_size
  // ==========================================
  chatFlowsRef.current.registerSkill('save_team_size', async (args) => {
    console.log('🔵 save_team_size called:', args);
    return await saveData('team_size', args.value);
  });

  // ==========================================
  // Skill 5: save_equipment
  // ==========================================
  chatFlowsRef.current.registerSkill('save_equipment', async (args) => {
    console.log('🔵 save_equipment called:', args);
    const result = await saveData('equipment', args.value);
    console.log('✅ save_equipment result:', result);
    return result;
  });

  // ==========================================
  // Skill 6: save_risks
  // ==========================================
  chatFlowsRef.current.registerSkill('save_risks', async (args) => {
    console.log('🔵 save_risks called:', args);
    const result = await saveData('risks', args.value);
    console.log('✅ save_risks result:', result);
    return result;
  });

  // ==========================================
  // Skill 7: save_project_description
  // ==========================================
  chatFlowsRef.current.registerSkill('save_project_description', async (args) => {
    console.log('🔵 save_project_description called:', args);
    const result = await saveData('project_description', args.value);
    console.log('✅ save_project_description result:', result);
    return result;
  });

  // ==========================================
  // Skill 8: save_work_setting
  // ==========================================
  chatFlowsRef.current.registerSkill('save_work_setting', async (args) => {
    console.log('🔵 save_work_setting called:', args);
    const result = await saveData('work_setting', args.value);
    console.log('✅ save_work_setting result:', result);
    return result;
  });

  // ==========================================
  // Skill 9: save_worker_roles
  // ==========================================
  chatFlowsRef.current.registerSkill('save_worker_roles', async (args) => {
    console.log('🔵 save_worker_roles called:', args);
    // worker_roles is stored as JSONB, so it can be an object or array
    const result = await saveData('worker_roles', args.value);
    console.log('✅ save_worker_roles result:', result);
    return result;
  });

  // ==========================================
  // Skill 10: save_hazardous_substances
  // ==========================================
  chatFlowsRef.current.registerSkill('save_hazardous_substances', async (args) => {
    console.log('🔵 save_hazardous_substances called:', args);
    const result = await saveData('hazardous_substances', args.value);
    console.log('✅ save_hazardous_substances result:', result);
    return result;
  });

  // ==========================================
  // Skill 11: save_environment
  // ==========================================
  chatFlowsRef.current.registerSkill('save_environment', async (args) => {
    console.log('🔵 save_environment called:', args);
    const result = await saveData('environment', args.value);
    console.log('✅ save_environment result:', result);
    return result;
  });

  // ==========================================
  // Skill 12: save_heights
  // ==========================================
  chatFlowsRef.current.registerSkill('save_heights', async (args) => {
    console.log('🔵 save_heights called:', args);
    const result = await saveData('heights', args.value);
    console.log('✅ save_heights result:', result);
    return result;
  });

  // ==========================================
  // Skill 13: save_incident_history
  // ==========================================
  chatFlowsRef.current.registerSkill('save_incident_history', async (args) => {
    console.log('🔵 save_incident_history called:', args);
    // This skill uses file_id instead of value
    const result = await saveData('incident_history_file_id', args.file_id);
    console.log('✅ save_incident_history result:', result);
    return result;
  });

  // ==========================================
  // Skill 14: save_accreditations
  // ==========================================
  chatFlowsRef.current.registerSkill('save_accreditations', async (args) => {
    console.log('🔵 save_accreditations called:', args);
    const result = await saveData('accreditations', args.value);
    console.log('✅ save_accreditations result:', result);
    return result;
  });

  // ==========================================
  // Skill 15: complete_section (DISABLED - no sections anymore)
  // ==========================================
  // Sections have been removed from the wizard flow
  // This skill is kept for backwards compatibility but does nothing
  chatFlowsRef.current.registerSkill('complete_section', async (args) => {
    console.log('🔵 complete_section called (DISABLED - no sections):', args);

    return {
      success: true,
      message: 'Sections are no longer used in this wizard',
      data: { note: 'This skill is deprecated' }
    };
  });

  // ==========================================
  // Skill 16: update_requirements
  // ==========================================
  chatFlowsRef.current.registerSkill('update_requirements', async (args) => {
    console.log('🔵 update_requirements called with args:', args);
    console.log('🔵 args type:', typeof args);
    console.log('🔵 args keys:', Object.keys(args || {}));

    // Handle different parameter formats
    // ChatFlows might pass: { updates: "..." } or { parameters: { updates: "..." } }
    let updatesParam = args.updates || args.parameters?.updates;
    
    if (!updatesParam) {
      console.error('❌ update_requirements: updates parameter is missing');
      console.error('❌ Available args:', JSON.stringify(args, null, 2));
      return {
        success: false,
        error: 'Missing updates parameter. Expected an object with action property.',
        received: args
      };
    }

    // Parse updates if it's a JSON string
    let updates = updatesParam;
    if (typeof updates === 'string') {
      try {
        updates = JSON.parse(updates);
        console.log('📦 Parsed updates from JSON string:', updates);
      } catch (parseError) {
        console.error('❌ Failed to parse updates JSON string:', parseError);
        return {
          success: false,
          error: `Invalid JSON in updates parameter: ${parseError.message}`
        };
      }
    }

    // Handle array of updates (process first one, or process all sequentially)
    if (Array.isArray(updates)) {
      console.log(`📋 Processing array of ${updates.length} updates...`);
      // For now, process the first update (can be extended to process all)
      if (updates.length === 0) {
        return {
          success: false,
          error: 'Updates array is empty'
        };
      }
      updates = updates[0]; // Process first update
      console.log('📝 Processing first update from array:', updates);
    }

    const action = updates.action;

    if (!action) {
      console.error('❌ update_requirements: action property is missing in updates');
      return {
        success: false,
        error: 'Missing action property in updates. Expected: "add", "remove", "replace", or "amend".'
      };
    }

    try {
      // Get current requirements from state
      let currentRequirements = [...(qualifications || [])];
      console.log(`📝 Processing ${action} action on requirements...`);

      // Helper function to resolve requirement ID from target (supports both id and name)
      const resolveRequirementId = (target) => {
        if (target.id) {
          return target.id;
        }
        if (target.name) {
          const found = currentRequirements.find(req => req.name === target.name);
          if (!found) {
            throw new Error(`Requirement not found with name: "${target.name}"`);
          }
          console.log(`🔍 Found requirement by name "${target.name}": ${found.id}`);
          return found.id;
        }
        throw new Error('Target must have either "id" or "name" property');
      };

      // Process the update based on action type
      switch (action) {
        case 'add': {
          // Add a new requirement
          if (!updates.item) {
            throw new Error('Missing "item" property for add action');
          }

          const newRequirement = {
            name: updates.item.name,
            description: updates.item.description || '',
            category: updates.item.category || '',
            priority: updates.item.priority || 'medium',
            renewal_period: updates.item.renewal_period || updates.item.renewalPeriod || null,
            status: updates.item.status || 'pending',
            regulatory_body: updates.item.regulatory_body || updates.item.regulatoryBody || '',
            regulation_reference: updates.item.regulation_reference || updates.item.regulationReference || '',
            legal_requirement: updates.item.legal_requirement || updates.item.legalRequirement || false,
            required_for: updates.item.required_for || updates.item.requiredFor || '',
            notes: updates.item.notes || '',
            minimum_personnel: updates.item.minimum_personnel || updates.item.minimumPersonnel || null,
            calculated_required: updates.item.calculated_required || updates.item.calculatedRequired || null,
            course_links: updates.item.course_links || updates.item.courseLinks || {}
          };

          console.log('💾 Adding new requirement to database...');
          const addResult = await complianceService.addRequirement(projectId, newRequirement);
          if (!addResult.success) {
            throw new Error(`Failed to add requirement: ${addResult.error}`);
          }

          currentRequirements.push(addResult.data);
          console.log('✅ Added requirement to database');
          break;
        }

        case 'remove': {
          // Remove a requirement by ID or name
          if (!updates.target) {
            throw new Error('Missing "target" property for remove action');
          }

          const requirementId = resolveRequirementId(updates.target);
          console.log(`🗑️ Removing requirement ${requirementId} from database...`);

          const deleteResult = await complianceService.deleteRequirement(requirementId);
          if (!deleteResult.success) {
            throw new Error(`Failed to delete requirement: ${deleteResult.error}`);
          }

          currentRequirements = currentRequirements.filter(req => req.id !== requirementId);
          console.log('✅ Removed requirement from database');
          break;
        }

        case 'replace': {
          // Replace a single field value
          if (!updates.target) {
            throw new Error('Missing "target" property for replace action');
          }
          if (!updates.path) {
            throw new Error('Missing "path" property for replace action');
          }
          if (updates.value === undefined) {
            throw new Error('Missing "value" property for replace action');
          }

          const requirementId = resolveRequirementId(updates.target);
          const fieldPath = updates.path;
          const newValue = updates.value;

          // Map field paths to database column names
          const fieldMapping = {
            priority: 'priority',
            name: 'name',
            description: 'description',
            category: 'category',
            renewal_period: 'renewal_period',
            renewalPeriod: 'renewal_period',
            status: 'status',
            regulatory_body: 'regulatory_body',
            regulatoryBody: 'regulatory_body',
            regulation_reference: 'regulation_reference',
            regulationReference: 'regulation_reference',
            legal_requirement: 'legal_requirement',
            legalRequirement: 'legal_requirement',
            required_for: 'required_for',
            requiredFor: 'required_for',
            notes: 'notes',
            minimum_personnel: 'minimum_personnel',
            minimumPersonnel: 'minimum_personnel',
            calculated_required: 'calculated_required',
            calculatedRequired: 'calculated_required'
          };

          const dbFieldName = fieldMapping[fieldPath] || fieldPath;
          console.log(`🔄 Replacing ${fieldPath} (${dbFieldName}) for requirement ${requirementId}...`);

          console.log(`💾 Updating requirement ${requirementId} in database: ${dbFieldName} = ${newValue}`);

          const updateResult = await complianceService.updateRequirement(requirementId, {
            [dbFieldName]: newValue
          });

          console.log('📊 Update result:', updateResult);

          if (!updateResult.success) {
            console.error('❌ Database update failed:', updateResult.error);
            throw new Error(`Failed to update requirement: ${updateResult.error}`);
          }

          // Update local state
          const index = currentRequirements.findIndex(req => req.id === requirementId);
          if (index !== -1) {
            console.log(`🔄 Updating local state at index ${index}`);
            currentRequirements[index] = updateResult.data;
            console.log('✅ Updated local state:', currentRequirements[index]);
          } else {
            console.warn(`⚠️ Requirement ${requirementId} not found in local state, reloading from database...`);
            // Reload from database if not found locally
            const allRequirementsResult = await complianceService.loadProjectCompliance(projectId);
            if (allRequirementsResult.success && allRequirementsResult.requirements) {
              currentRequirements = allRequirementsResult.requirements;
              const reloadedIndex = currentRequirements.findIndex(req => req.id === requirementId);
              if (reloadedIndex !== -1) {
                currentRequirements[reloadedIndex] = updateResult.data;
                console.log('✅ Reloaded and updated requirement from database');
              }
            }
          }

          console.log('✅ Replaced field in database and state');
          break;
        }

        case 'amend': {
          // Update multiple fields
          if (!updates.target) {
            throw new Error('Missing "target" property for amend action');
          }
          if (!updates.changes || typeof updates.changes !== 'object') {
            throw new Error('Missing or invalid "changes" property for amend action');
          }

          const requirementId = resolveRequirementId(updates.target);
          const changes = updates.changes;

          // Map field names to database column names
          const fieldMapping = {
            priority: 'priority',
            name: 'name',
            description: 'description',
            category: 'category',
            renewal_period: 'renewal_period',
            renewalPeriod: 'renewal_period',
            status: 'status',
            regulatory_body: 'regulatory_body',
            regulatoryBody: 'regulatory_body',
            regulation_reference: 'regulation_reference',
            regulationReference: 'regulation_reference',
            legal_requirement: 'legal_requirement',
            legalRequirement: 'legal_requirement',
            required_for: 'required_for',
            requiredFor: 'required_for',
            notes: 'notes',
            minimum_personnel: 'minimum_personnel',
            minimumPersonnel: 'minimum_personnel',
            calculated_required: 'calculated_required',
            calculatedRequired: 'calculated_required',
            course_links: 'course_links',
            courseLinks: 'course_links'
          };

          const dbChanges = {};
          for (const [key, value] of Object.entries(changes)) {
            const dbFieldName = fieldMapping[key] || key;
            dbChanges[dbFieldName] = value;
          }

          console.log(`🔄 Amending multiple fields for requirement ${requirementId}...`);

          const updateResult = await complianceService.updateRequirement(requirementId, dbChanges);
          if (!updateResult.success) {
            throw new Error(`Failed to update requirement: ${updateResult.error}`);
          }

          // Update local state
          const index = currentRequirements.findIndex(req => req.id === requirementId);
          if (index !== -1) {
            currentRequirements[index] = updateResult.data;
          }

          console.log('✅ Amended requirement in database');
          break;
        }

        default:
          throw new Error(`Unknown action: ${action}. Expected: "add", "remove", "replace", or "amend".`);
      }

      // Update state with modified requirements
      console.log('🔄 Updating local state with', currentRequirements.length, 'requirements...');
      setQualifications(currentRequirements);
      console.log('✅ State updated');

      console.log(`✅ Successfully processed ${action} action. Total requirements: ${currentRequirements.length}`);

      // Update embed context - always send all data
      if (chatFlowsRef.current?.update && getAllEmbedData) {
        const allEmbedData = getAllEmbedData();
        // Include generated_requirements (full updated array)
        allEmbedData.generated_requirements = currentRequirements;
        
        console.log('📤 Sending embed_update with', currentRequirements.length, 'requirements');
        chatFlowsRef.current.update(allEmbedData);
        console.log('✅ Embed update sent');
      } else {
        console.warn('⚠️ chatFlowsRef.current.update or getAllEmbedData not available');
      }

      // Return the full updated requirements array (as specified in system prompt)
      const result = {
        success: true,
        message: `Successfully processed ${action} action`,
        data: currentRequirements  // Return full array as required
      };
      console.log('✅ Returning result:', { 
        success: result.success, 
        message: result.message, 
        dataLength: result.data?.length 
      });
      return result;
    } catch (error) {
      console.error('❌ Error updating requirements:', error);
      console.error('❌ Error stack:', error.stack);
      return {
        success: false,
        error: error.message,
        stack: error.stack
      };
    }
  });

  // ==========================================
  // Skill 17: generate_requirements
  // ==========================================
  chatFlowsRef.current.registerSkill('generate_requirements', async (args) => {
    console.log('🔵 generate_requirements called:', args);

    try {
      setIsGenerating(true);

      console.log('🚀 Starting compliance requirements generation...');

      // Start generation in background (don't await - let it run)
      (async () => {
        try {
          await generateRequirementsFromWizard();

          // Note: setIsGenerating(false) is called by handleWizardSubmit, no need to call it again here

          console.log('✅ Requirements generation completed');

          // Update ChatFlows - always send all data
          if (chatFlowsRef.current?.update && getAllEmbedData) {
            const allEmbedData = getAllEmbedData();
            console.log('📤 Sending updated data to ChatFlows, requirements count:', allEmbedData.generated_requirements?.length || 0);
            chatFlowsRef.current.update(allEmbedData);
          }
        } catch (error) {
          console.error('❌ Error generating requirements:', error);
          setIsGenerating(false);
        }
      })();

      // Return immediately as COMPLETED (generation runs in background)
      return {
        success: true,
        message: 'Compliance requirements generation started successfully',
        data: {
          status: 'generating',
          message: 'Requirements are being generated and will appear on your page shortly'
        }
      };
    } catch (error) {
      console.error('❌ Error starting requirements generation:', error);
      setIsGenerating(false);

      return {
        success: false,
        error: error.message
      };
    }
  });

  console.log('✅ ChatFlows compliance skills registered (16 active skills, 1 deprecated)');
};

/**
 * Send initial context data to ChatFlows embed on load
 *
 * @param {Object} chatFlowsRef - Ref to ChatFlowsEmbed component
 * @param {Object} context - Context object with data
 */
export const sendInitialContext = (chatFlowsRef, context) => {
  const {
    getAllEmbedData
  } = context;

  if (!chatFlowsRef.current) {
    console.warn('ChatFlows ref not available for context update');
    return;
  }

  console.log('📤 Sending initial context to ChatFlows...');

  // Use getAllEmbedData to get consistent data structure
  if (getAllEmbedData) {
    const contextData = getAllEmbedData();
    
    // Send context update to iframe
    chatFlowsRef.current.update(contextData);

    console.log('✅ Initial context sent to ChatFlows embed');
    console.log('📊 Context summary:', {
      fields: Object.keys(contextData),
      teamMembers_count: contextData.teamMembers?.length || 0
    });
  } else {
    console.warn('⚠️ getAllEmbedData not available in context');
  }
};

/**
 * Get context summary for debugging
 */
export const getContextSummary = (context) => {
  return {
    wizard: {
      currentSection: context.wizardState?.currentSection,
      completedSections: context.wizardState?.completedSections?.length || 0,
      hasData: Object.keys(context.wizardState?.collectedData || {}).length > 0
    },
    team: {
      size: context.teamMembers?.length || 0,
      members: context.teamMembers?.map(m => m.name) || []
    },
    requirements: {
      count: context.qualifications?.length || 0,
      generated: (context.qualifications?.length || 0) > 0
    },
    project: {
      id: context.projectId,
      name: context.space?.name,
      industry: context.space?.industry
    }
  };
};
